import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 px-4 pt-4" data-testid="navbar">
      <div
        className={`max-w-5xl mx-auto transition-all duration-500 ${
          isScrolled
            ? 'bg-white/80 backdrop-blur-xl shadow-elevated rounded-full py-2 px-6'
            : 'bg-transparent py-3 px-6'
        }`}
      >
        <div className="flex items-center justify-between">
          {/* Logo - only visible when scrolled */}
          <Link to="/" className={`flex-shrink-0 transition-all duration-500 ${isScrolled ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} data-testid="navbar-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_cd6d2b09-b4b1-476c-b8b1-025cb392e532/artifacts/hmn5ph1q_IMG_3888.jpeg"
              alt="Hooyo's Place"
              className="h-14 w-auto"
              style={{
                maskImage: 'radial-gradient(ellipse 70% 75% at 50% 50%, black 35%, transparent 68%)',
                WebkitMaskImage: 'radial-gradient(ellipse 70% 75% at 50% 50%, black 35%, transparent 68%)',
              }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            <button
              onClick={() => scrollToSection('about')}
              data-testid="navbar-about-link"
              className="relative px-4 py-2 text-foreground/80 hover:text-foreground font-semibold text-sm rounded-full hover:bg-primary/8 transition-all duration-300"
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('programs')}
              data-testid="navbar-programs-link"
              className="relative px-4 py-2 text-foreground/80 hover:text-foreground font-semibold text-sm rounded-full hover:bg-primary/8 transition-all duration-300"
            >
              Programs
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              data-testid="navbar-contact-link"
              className="relative px-4 py-2 text-foreground/80 hover:text-foreground font-semibold text-sm rounded-full hover:bg-primary/8 transition-all duration-300"
            >
              Contact
            </button>
            <Link to="/schedule-tour" data-testid="navbar-tour-link">
              <span className="relative px-4 py-2 text-secondary hover:text-secondary/80 font-bold text-sm rounded-full hover:bg-secondary/8 transition-all duration-300 inline-block">
                Schedule a Tour
              </span>
            </Link>
            <Link to="/parent-portal" data-testid="navbar-parent-link">
              <span className="relative px-4 py-2 text-success hover:text-success/80 font-bold text-sm rounded-full hover:bg-success/8 transition-all duration-300 inline-block">
                Parent Portal
              </span>
            </Link>
            <Link to="/register" data-testid="navbar-enroll-link">
              <Button className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-6 py-2 text-sm font-bold shadow-soft hover:shadow-glow transition-all duration-300 hover:scale-105 active:scale-95 ml-1">
                Enroll Now
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-full hover:bg-muted transition-all"
            aria-label="Toggle menu"
            data-testid="navbar-mobile-toggle"
          >
            {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden mt-2 mx-auto max-w-5xl bg-white/95 backdrop-blur-xl rounded-3xl shadow-elevated p-4 space-y-1 animate-fade-in" data-testid="navbar-mobile-menu">
          <button onClick={() => scrollToSection('about')} data-testid="navbar-mobile-about" className="block w-full text-left py-3 px-5 text-foreground hover:text-primary hover:bg-primary/5 rounded-2xl transition-all font-semibold">
            About
          </button>
          <button onClick={() => scrollToSection('programs')} data-testid="navbar-mobile-programs" className="block w-full text-left py-3 px-5 text-foreground hover:text-primary hover:bg-primary/5 rounded-2xl transition-all font-semibold">
            Programs
          </button>
          <button onClick={() => scrollToSection('contact')} data-testid="navbar-mobile-contact" className="block w-full text-left py-3 px-5 text-foreground hover:text-primary hover:bg-primary/5 rounded-2xl transition-all font-semibold">
            Contact
          </button>
          <Link to="/schedule-tour" onClick={() => setIsOpen(false)}>
            <span data-testid="navbar-mobile-tour" className="block w-full text-left py-3 px-5 text-secondary font-bold hover:bg-secondary/5 rounded-2xl transition-all">
              Schedule a Tour
            </span>
          </Link>
          <Link to="/parent-portal" onClick={() => setIsOpen(false)}>
            <span data-testid="navbar-mobile-parent" className="block w-full text-left py-3 px-5 text-success font-bold hover:bg-success/5 rounded-2xl transition-all">
              Parent Portal
            </span>
          </Link>
          <Link to="/register" onClick={() => setIsOpen(false)}>
            <Button data-testid="navbar-mobile-enroll" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-full py-3 mt-2 font-bold">
              Enroll Now
            </Button>
          </Link>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
